"use client";

import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  HStack,
  Input,
  Text,
  Box,
  IconButton,
  Flex,
  useOutsideClick,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash2, X } from "lucide-react";

interface DynamicVariable {
  key: string;
  value: string;
}

interface DynamicVariablesButtonProps {
  onVariablesChange: (variables: Record<string, string>) => void;
  prompt?: string;
  beginMessage?: string;
}

export const DynamicVariablesButton: React.FC<DynamicVariablesButtonProps> = ({
  onVariablesChange,
  prompt = "",
  beginMessage = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [savedVariables, setSavedVariables] = useState<DynamicVariable[]>([]);
  const [stagedVariables, setStagedVariables] = useState<DynamicVariable[]>([]);
  const [newFields, setNewFields] = useState<DynamicVariable[]>([]);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const previousVariablesRef = useRef<Record<string, string>>({});
  const toast = useToast();

  useOutsideClick({
    ref: popoverContentRef,
    handler: () => setIsOpen(false),
  });

  const extractVariables = useCallback((text: string): string[] => {
    const regex = /{{([^}]+)}}/g;
    const matches = text.match(regex);
    if (!matches) return [];
    return matches.map((match) => match.slice(2, -2).trim());
  }, []);

  useEffect(() => {
    const promptVars = extractVariables(prompt);
    const beginMessageVars = extractVariables(beginMessage);
    const uniqueVars = Array.from(
      new Set([...promptVars, ...beginMessageVars])
    );

    if (uniqueVars.length > 0) {
      const newVars = uniqueVars.map((key) => ({ key, value: "" }));
      setSavedVariables(newVars);

      if (isOpen) setStagedVariables([...newVars]);
    }
  }, [prompt, beginMessage, extractVariables]);

  useEffect(() => {
    if (isOpen) {
      setStagedVariables([...savedVariables]);
      setNewFields([]);
    }
  }, [isOpen, savedVariables]);

  const updateParent = useCallback(
    (vars: DynamicVariable[]) => {
      const variablesObject = vars.reduce((acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value;
        return acc;
      }, {} as Record<string, string>);

      const prevKeys = Object.keys(previousVariablesRef.current);
      const currentKeys = Object.keys(variablesObject);

      if (prevKeys.length !== currentKeys.length) {
        previousVariablesRef.current = variablesObject;
        onVariablesChange(variablesObject);
        return;
      }

      let hasChanged = false;
      for (const key of currentKeys) {
        if (
          previousVariablesRef.current[key] !== variablesObject[key] ||
          !previousVariablesRef.current.hasOwnProperty(key)
        ) {
          hasChanged = true;
          break;
        }
      }

      if (hasChanged) {
        previousVariablesRef.current = variablesObject;
        onVariablesChange(variablesObject);
      }
    },
    [onVariablesChange]
  );

  useEffect(() => {
    updateParent(savedVariables);
  }, [savedVariables, updateParent]);

  const handleAddNewField = () => {
    setNewFields([...newFields, { key: "", value: "" }]);
  };

  const handleRemoveNewField = (index: number) => {
    const updatedFields = [...newFields];
    updatedFields.splice(index, 1);
    setNewFields(updatedFields);
  };

  const handleRemoveVariable = (index: number) => {
    const updatedVariables = [...stagedVariables];
    updatedVariables.splice(index, 1);
    setStagedVariables(updatedVariables);
  };

  const handleSave = () => {
    const validNewFields = newFields.filter((field) => field.key.trim() !== "");

    const allKeys = new Set(stagedVariables.map((v) => v.key));
    for (const field of validNewFields) {
      if (allKeys.has(field.key)) {
        toast({
          title: `Duplicate variable name: ${field.key}`,
          status: "error",
          duration: 2000,
        });
        return;
      }
      allKeys.add(field.key);
    }

    const combinedVariables = [...stagedVariables, ...validNewFields];
    setSavedVariables(combinedVariables);
    setIsOpen(false);
  };

  const handleCancel = () => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => {}}
      placement="bottom-end"
      closeOnBlur={false}
      autoFocus={false}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
          fontFamily="monospace"
          fontWeight="normal"
          px={2}
          minW="auto"
          color="black"
          _hover={{ bg: "orange.50", color: "orange.700" }}
          _active={{ bg: "orange.100" }}
          _focusVisible={{
            boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
          }}
        >
          {"{}"}
          {savedVariables.length > 0 && (
            <Box
              as="span"
              ml={1}
              bg="orange.100"
              color="orange.800"
              px={1.5}
              py={0.5}
              borderRadius="full"
              fontSize="xs"
            >
              {savedVariables.length}
            </Box>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        ref={popoverContentRef}
        boxShadow="lg"
        width="550px"
        maxWidth="95vw"
        borderColor="orange.200"
        _focusWithin={{
          borderColor: "orange.300",
          boxShadow: "0 0 0 1px var(--chakra-colors-orange-300)",
        }}
      >
        <PopoverBody p={4}>
          <VStack spacing={4} align="stretch">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="bold" fontSize="lg">
                Dynamic Variables
              </Text>
              <IconButton
                aria-label="Close"
                icon={<X size={16} />}
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                color="gray.700"
                _hover={{ bg: "orange.50", color: "orange.700" }}
                _active={{ bg: "orange.100" }}
                _focusVisible={{
                  boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                }}
              />
            </Flex>

            <Text fontSize="sm" opacity={0.7}>
              Set dynamic variables for dashboard audio and llm tests
            </Text>

            <Box
              bg="gray.50"
              p={3}
              borderRadius="md"
              maxH="260px"
              overflowY="auto"
              sx={{
                scrollbarWidth: "thin",
                scrollbarColor: "var(--chakra-colors-orange-400) transparent",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "var(--chakra-colors-orange-400)",
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "var(--chakra-colors-orange-500)",
                },
              }}
            >
              <Flex mb={2} fontWeight="medium" fontSize="sm">
                <Box flex="1">Variable Name</Box>
                <Box flex="1">Test Value</Box>
                <Box width="40px"></Box>
              </Flex>

              {stagedVariables.map((variable, index) => (
                <HStack key={`staged-${index}`} mb={2}>
                  <Input
                    value={variable.key}
                    isReadOnly
                    placeholder="Variable name"
                    size="md"
                    focusBorderColor="orange.400"
                  />
                  <Input
                    value={variable.value}
                    onChange={(e) => {
                      const updatedVariables = [...stagedVariables];
                      updatedVariables[index].value = e.target.value;
                      setStagedVariables(updatedVariables);
                    }}
                    placeholder="Enter the value"
                    size="md"
                    focusBorderColor="orange.400"
                  />
                  <IconButton
                    aria-label="Remove variable"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveVariable(index)}
                    opacity={0.6}
                    _hover={{ opacity: 1, color: "red.500" }}
                    color="gray.700"
                  />
                </HStack>
              ))}

              {newFields.map((field, index) => (
                <HStack key={`new-${index}`} mb={2}>
                  <Input
                    value={field.key}
                    onChange={(e) => {
                      const updatedFields = [...newFields];
                      updatedFields[index].key = e.target.value;
                      setNewFields(updatedFields);
                    }}
                    placeholder="Enter the variable name"
                    size="md"
                    focusBorderColor="orange.400"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const updatedFields = [...newFields];
                      updatedFields[index].value = e.target.value;
                      setNewFields(updatedFields);
                    }}
                    placeholder="Enter the value"
                    size="md"
                    focusBorderColor="orange.400"
                  />
                  <IconButton
                    aria-label="Remove field"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveNewField(index)}
                    _hover={{ color: "red.500" }}
                    color="gray.700"
                  />
                </HStack>
              ))}

              <Button
                leftIcon={<Plus size={16} />}
                size="sm"
                variant="outline"
                onClick={handleAddNewField}
                mt={2}
                colorScheme="orange"
                color="orange.600"
                borderColor="orange.400"
                _hover={{ bg: "orange.50", borderColor: "orange.500" }}
                _active={{ bg: "orange.100", borderColor: "orange.600" }}
                _focusVisible={{
                  borderColor: "orange.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                }}
              >
                Add
              </Button>
            </Box>

            <Flex justifyContent="flex-end" gap={2} mt={2}>
              <Button
                size="sm"
                onClick={handleCancel}
                variant="ghost"
                color="black"
                _hover={{ bg: "gray.100" }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                colorScheme="orange"
                bg="orange.500"
                _hover={{ bg: "orange.600" }}
                _active={{ bg: "orange.700" }}
                _focusVisible={{
                  boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                }}
              >
                Save
              </Button>
            </Flex>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
